import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Building, MapPin, Phone, Globe, Hash, Mail, User, Facebook, Instagram, Twitter, Linkedin, Youtube, Share2 } from 'lucide-react';
import { Form } from '@/components/ui/form';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/ui/use-toast';
import { SettingsCard, IconInputField, EditControls } from '../ui';
import { 
  ExtendedBusinessDetails, 
  BusinessBasicDetails, 
  createBusinessDefaults,
  resetBusinessForm 
} from '../utils';

// Schema for business basic details only
const socialUrlSchema = z.string().url("Please enter a valid URL starting with https://").optional().or(z.literal(""));

const businessBasicDetailsSchema = z.object({
  name: z.string().min(2, "Business name must be at least 2 characters"),
  address: z.string().min(5, "Please enter a valid address"),
  website: z.string().url("Please enter a valid website URL").optional().or(z.literal("")),
  phone: z.string().min(5, "Please enter a valid phone number").optional().or(z.literal("")),
  google_place_id: z.string().optional().or(z.literal("")),
  sender_name: z.string().optional().or(z.literal("")),
  reply_to: z.string().email("Please enter a valid email address").optional().or(z.literal("")),
  latitude: z.string().optional().or(z.literal("")),
  longitude: z.string().optional().or(z.literal("")),
  // Social media links
  facebook_url: socialUrlSchema,
  instagram_url: socialUrlSchema,
  twitter_url: socialUrlSchema,
  linkedin_url: socialUrlSchema,
  youtube_url: socialUrlSchema,
});

interface BusinessBasicDetailsFormProps {
  businessDetails: ExtendedBusinessDetails;
  onUpdateSuccess: (updatedDetails: Partial<ExtendedBusinessDetails>) => void;
}

export const BusinessBasicDetailsForm: React.FC<BusinessBasicDetailsFormProps> = ({
  businessDetails,
  onUpdateSuccess,
}) => {
  const [loading, setLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const emailSectionRef = React.useRef<HTMLDivElement>(null);
  const locationSectionRef = React.useRef<HTMLDivElement>(null);

  const form = useForm<BusinessBasicDetails>({
    resolver: zodResolver(businessBasicDetailsSchema),
    defaultValues: createBusinessDefaults(businessDetails),
  });

  // Effect to reset form when businessDetails prop changes
  useEffect(() => {
    form.reset(createBusinessDefaults(businessDetails));
  }, [businessDetails, form]);

  // Effect to handle email sender highlighting from EmailTemplateBuilder
  useEffect(() => {
    const shouldHighlight = sessionStorage.getItem('highlightEmailSender');

    if (shouldHighlight === 'true' && emailSectionRef.current) {
      // Clear the flag
      sessionStorage.removeItem('highlightEmailSender');

      // Wait for render, then scroll and highlight
      setTimeout(() => {
        if (emailSectionRef.current) {
          // Scroll to the email section
          emailSectionRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'center'
          });

          // Add highlight classes
          emailSectionRef.current.classList.add(
            'ring-2',
            'ring-blue-500',
            'ring-offset-2',
            'rounded-lg',
            'transition-all',
            'duration-300'
          );

          // Auto-click "Start Editing Settings" button if not already editing
          if (!isEditing) {
            setIsEditing(true);
          }

          // Remove highlight after 3 seconds
          setTimeout(() => {
            if (emailSectionRef.current) {
              emailSectionRef.current.classList.remove(
                'ring-2',
                'ring-blue-500',
                'ring-offset-2',
                'transition-all',
                'duration-300'
              );
            }
          }, 3000);
        }
      }, 300);
    }
  }, [isEditing]);

  // Effect to handle location highlighting from LoyaltyPassBuilder
  useEffect(() => {
    const shouldHighlight = sessionStorage.getItem('highlightLocation');

    if (shouldHighlight === 'true' && locationSectionRef.current) {
      // Clear the flag
      sessionStorage.removeItem('highlightLocation');

      // Wait for render, then scroll and highlight
      setTimeout(() => {
        if (locationSectionRef.current) {
          // Scroll to the location section
          locationSectionRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'center'
          });

          // Add highlight classes
          locationSectionRef.current.classList.add(
            'ring-2',
            'ring-blue-500',
            'ring-offset-2',
            'rounded-lg',
            'transition-all',
            'duration-300'
          );

          // Auto-click "Start Editing Settings" button if not already editing
          if (!isEditing) {
            setIsEditing(true);
          }

          // Remove highlight after 3 seconds
          setTimeout(() => {
            if (locationSectionRef.current) {
              locationSectionRef.current.classList.remove(
                'ring-2',
                'ring-blue-500',
                'ring-offset-2',
                'transition-all',
                'duration-300'
              );
            }
          }, 3000);
        }
      }, 300);
    }
  }, [isEditing]);

  const onSubmit = async (values: BusinessBasicDetails) => {
    setLoading(true);
    try {
      // Parse and validate coordinates
      let parsedLatitude: number | null = null;
      let parsedLongitude: number | null = null;
      let locationSource = businessDetails.location_source;

      if (values.latitude && values.longitude) {
        const lat = parseFloat(values.latitude);
        const lng = parseFloat(values.longitude);

        // Validate latitude range (-90 to 90)
        if (isNaN(lat) || lat < -90 || lat > 90) {
          toast.error("Invalid latitude value (must be between -90 and 90)");
          setLoading(false);
          return;
        }

        // Validate longitude range (-180 to 180)
        if (isNaN(lng) || lng < -180 || lng > 180) {
          toast.error("Invalid longitude value (must be between -180 and 180)");
          setLoading(false);
          return;
        }

        parsedLatitude = lat;
        parsedLongitude = lng;

        // If manually edited and different from current, mark as manual
        if (businessDetails.latitude !== null &&
            businessDetails.longitude !== null &&
            (Math.abs(parseFloat(businessDetails.latitude.toString()) - lat) > 0.000001 ||
             Math.abs(parseFloat(businessDetails.longitude.toString()) - lng) > 0.000001)) {
          locationSource = 'manual';
        } else if (locationSource === null || locationSource === 'auto') {
          locationSource = 'manual';
        }
      }

      const updates = {
        name: values.name,
        address: values.address,
        website: values.website || null,
        phone: values.phone || null,
        google_place_id: values.google_place_id || null,
        sender_name: values.sender_name || values.name, // Default to business name if empty
        reply_to: values.reply_to || null,
        latitude: parsedLatitude,
        longitude: parsedLongitude,
        location_source: (parsedLatitude !== null && parsedLongitude !== null) ? locationSource : null,
        location_last_updated: (parsedLatitude !== null && parsedLongitude !== null) ? new Date().toISOString() : null,
        // Social media links
        facebook_url: values.facebook_url || null,
        instagram_url: values.instagram_url || null,
        twitter_url: values.twitter_url || null,
        linkedin_url: values.linkedin_url || null,
        youtube_url: values.youtube_url || null,
        updated_at: new Date().toISOString(),
      };

      const { error } = await supabase
        .from("businesses")
        .update(updates)
        .eq("id", businessDetails.id);

      if (error) throw error;

      toast.success("Business details updated successfully!");
      setIsEditing(false);
      onUpdateSuccess(updates);
    } catch (error: unknown) {
      console.error("Error updating business:", error);
      toast.error(`Failed to update business: ${error instanceof Error ? error.message : String(error)}`);
    } finally {
      setLoading(false);
    }
  };

  const handleStartEditing = () => {
    setIsEditing(true);
  };

  const handleCancelEditing = () => {
    setIsEditing(false);
    resetBusinessForm(form, businessDetails);
  };

  return (
    <SettingsCard 
      icon={Building} 
      title="Business Settings" 
      description="Update your business information and details"
    >
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <IconInputField
            name="name"
            label="Business Name"
            placeholder="Coffee Shop"
            icon={Building}
            disabled={!isEditing}
            form={form}
          />
          
          <IconInputField
            name="address"
            label="Business Address"
            placeholder="123 Main St, City, Country"
            icon={MapPin}
            disabled={!isEditing}
            form={form}
          />

          <IconInputField
            name="phone"
            label="Phone Number"
            placeholder="+1 234 567 890"
            icon={Phone}
            disabled={!isEditing}
            form={form}
          />

          <IconInputField
            name="website"
            label="Website"
            placeholder="https://example.com"
            icon={Globe}
            disabled={!isEditing}
            form={form}
          />

          <IconInputField
            name="google_place_id"
            label="Google Place ID"
            placeholder="ChIJ..."
            icon={Hash}
            disabled={!isEditing}
            form={form}
            description="Optional: For Google Maps integration only"
          />

          {/* Location Coordinates Section */}
          <div ref={locationSectionRef} className="border-t pt-4 mt-4">
            <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2 flex items-center gap-2">
              <MapPin className="h-4 w-4" />
              Location Coordinates
            </h4>
            <p className="text-xs text-gray-600 dark:text-gray-400 mb-3">
              Used for location-based notifications on Apple and Google Wallet passes.
              {businessDetails.latitude && businessDetails.longitude && (
                <span className="ml-1 text-blue-600 dark:text-blue-400">
                  ({businessDetails.location_source === 'auto' ? 'Auto-detected from Google Places' : 'Manually set'})
                </span>
              )}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <IconInputField
                name="latitude"
                label="Latitude"
                placeholder="48.1486"
                icon={MapPin}
                disabled={!isEditing}
                form={form}
              />
              <IconInputField
                name="longitude"
                label="Longitude"
                placeholder="17.1077"
                icon={MapPin}
                disabled={!isEditing}
                form={form}
              />
            </div>
          </div>

          {/* Email Configuration Section */}
          <div ref={emailSectionRef} className="border-t pt-4 mt-4">
            <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
              <Mail className="h-4 w-4" />
              Email Sender Configuration
            </h4>
            <div className="space-y-4">
              <IconInputField
                name="sender_name"
                label="Email Sender Name"
                placeholder={`${businessDetails.name || 'Your Business'}`}
                icon={User}
                disabled={!isEditing}
                form={form}
                description="Name that appears in the 'From' field of emails (defaults to business name)"
              />
              <IconInputField
                name="reply_to"
                label="Reply-To Email"
                placeholder="reply@yourbusiness.com"
                icon={Mail}
                disabled={!isEditing}
                form={form}
                description="Email address where customers' replies will be sent"
              />
            </div>
          </div>

          {/* Social Media Links Section */}
          <div className="border-t pt-4 mt-4">
            <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2 flex items-center gap-2">
              <Share2 className="h-4 w-4" />
              Social Media Links
            </h4>
            <p className="text-xs text-gray-600 dark:text-gray-400 mb-3">
              Add your social media profiles. These will be automatically used in email templates.
            </p>
            <div className="space-y-4">
              <IconInputField
                name="facebook_url"
                label="Facebook"
                placeholder="https://facebook.com/yourbusiness"
                icon={Facebook}
                disabled={!isEditing}
                form={form}
              />
              <IconInputField
                name="instagram_url"
                label="Instagram"
                placeholder="https://instagram.com/yourbusiness"
                icon={Instagram}
                disabled={!isEditing}
                form={form}
              />
              <IconInputField
                name="twitter_url"
                label="Twitter / X"
                placeholder="https://x.com/yourbusiness"
                icon={Twitter}
                disabled={!isEditing}
                form={form}
              />
              <IconInputField
                name="linkedin_url"
                label="LinkedIn"
                placeholder="https://linkedin.com/company/yourbusiness"
                icon={Linkedin}
                disabled={!isEditing}
                form={form}
              />
              <IconInputField
                name="youtube_url"
                label="YouTube"
                placeholder="https://youtube.com/@yourbusiness"
                icon={Youtube}
                disabled={!isEditing}
                form={form}
              />
            </div>
          </div>

          <EditControls
            isEditing={isEditing}
            loading={loading}
            onStartEdit={handleStartEditing}
            onCancel={handleCancelEditing}
            startEditText="Edit Settings"
            saveText="Update Settings"
            className="mt-6 border-t border-border"
          />
        </form>
      </Form>
    </SettingsCard>
  );
};